import {
    getTopicGridCellsMap,
    manageGridCells
} from '@containers/activity-router/routes/helpers/grid/cells/grid-cells-helpers';
import {
    updateGridCellCounters
} from '@containers/activity-router/routes/helpers/grid/counters/grid-cell-counters/grid-cell-counters';
import {
    generateInitialGridCounters
} from '@containers/activity-router/routes/helpers/grid/counters/initial-grid-counters/initial-grid-counters';
import {
    isInstructionHubBlockedAssignment
} from '@containers/activity-router/routes/helpers/instruction-hub/instruction-hub-helpers';
import {
    InstructionHubBlockedKeys
} from '@containers/activity-router/routes/helpers/instruction-hub/instruction-hub.types';
import { parseActivityKey } from '@containers/activity-router/routes/helpers/keys/key-generator';
import {
    GetUpdatedGridDataParams,
    ManageFilteredGridCountersParams,
    ToggleGridCellsProps
} from '@containers/activity-router/routes/helpers/slice/grid/slice-grid-helpers.types';
import { getEmptyAssignmentsSelected } from '@containers/activity-router/routes/helpers/slice/slice-defaults';
import {
    GridCells,
    ToggleGridCell,
    TopicActivityCount
} from '@containers/activity-router/routes/helpers/slice/topic/topic-slice.types';

export const toggleGridCells = ({
    gridCells,
    gridCounters,
    studentId,
    topicId,
    activityId,
    isSelected
}: ToggleGridCellsProps) => {
    gridCells = manageGridCells({
        topicId,
        studentId,
        activityId,
        gridCells,
        isSelected
    });
    gridCounters = updateGridCellCounters({
        counters: gridCounters,
        isSelected,
        studentId,
        topicId,
        activityId
    });

    return {
        gridCells,
        gridCounters
    };
};

// A collapsed topic cell toggles every activity in the topic. When some of them are IH-owned for this
// student, toggle the others one by one instead, so IH activities are never selected or counted.
export const getGridCellTogglePayloads = <T extends Omit<ToggleGridCell, 'studentAssignments'>>(
    payload: T,
    gridCells: GridCells,
    instructionHubBlockedKeys?: InstructionHubBlockedKeys
): T[] => {
    const studentKey = payload.student.userProfileId.toString();
    const topicCells = gridCells[studentKey]?.[payload.topicId];

    if (payload.activityId || !topicCells) return [payload];

    const activityKeys = Object.keys(topicCells);
    const isBlocked = (activityKey: string) => isInstructionHubBlockedAssignment({
        instructionHubBlockedKeys,
        studentKey,
        activityKey
    });

    if (!activityKeys.some(isBlocked)) return [payload];

    return activityKeys
        .filter((activityKey) => !isBlocked(activityKey) && topicCells[activityKey].isSelected !== payload.isSelected)
        .map((activityKey) => ({
            ...payload,
            activityId: activityKey,
            activityType: parseActivityKey(activityKey).activityType
        }));
};

// IH-owned activities are never selected, so a collapsed topic cell counts only the activities the
// student can select when deciding whether it shows as selected. Unchanged topics keep their object,
// so memoised topic rows don't re-render.
export const getSelectableTopicActivityCount = (
    topicActivityCount: TopicActivityCount,
    gridCells: GridCells,
    instructionHubBlockedKeys?: InstructionHubBlockedKeys
): TopicActivityCount => {
    if (!instructionHubBlockedKeys) return topicActivityCount;

    let selectableCount = topicActivityCount;

    Object.entries(instructionHubBlockedKeys).forEach(([studentKey, blockedActivities]) => {
        Object.entries(gridCells[studentKey] || {}).forEach(([topicKey, topicCells]) => {
            const topicCount = topicActivityCount[studentKey]?.[topicKey];
            const blockedCount = Object.keys(topicCells).filter((activityKey) => blockedActivities[activityKey]).length;

            if (!topicCount || !blockedCount) return;

            selectableCount = {
                ...selectableCount,
                [studentKey]: {
                    ...selectableCount[studentKey],
                    [topicKey]: {
                        ...topicCount,
                        totalActivities: Math.max(topicCount.totalActivities - blockedCount, 0)
                    }
                }
            };
        });
    });

    return selectableCount;
};

const getInitialGridValues = ({
    studentListData,
    updatedFilteredCourse
}: Pick<GetUpdatedGridDataParams, 'studentListData' | 'updatedFilteredCourse'>) => {
    const updatedGridCounters = generateInitialGridCounters({
        selectedGroupStudents: studentListData?.selectedGroupStudents || [],
        topics: updatedFilteredCourse?.topics || []
    });
    const updatedGridCells = getTopicGridCellsMap({
        selectedGroupStudents: studentListData?.selectedGroupStudents || [],
        topics: updatedFilteredCourse?.topics || []
    });

    return {
        updatedGridCounters,
        updatedGridCells
    };
};
const manageFilteredGridCounters = ({
    isSelected,
    updatedGridCounters,
    studentKey,
    topicKey,
    activityKey
}: ManageFilteredGridCountersParams) => {
    if (isSelected) {
        updatedGridCounters = updateGridCellCounters({
            counters: updatedGridCounters,
            isSelected,
            studentId: studentKey,
            topicId: topicKey,
            activityId: activityKey
        });
    }

    return updatedGridCounters;
};

export const getUpdatedFilteredGridData = ({
    prevGridCells,
    studentListData,
    updatedFilteredCourse,
    manageSelectedAssignmentsCallback,
    studentAssignments
}: GetUpdatedGridDataParams) => {
    const {
        updatedGridCells,
        updatedGridCounters: initialGridCounters
    } = getInitialGridValues({
        studentListData,
        updatedFilteredCourse
    });

    let updatedSelectedAssignments = getEmptyAssignmentsSelected();
    let updatedGridCounters = initialGridCounters;

    Object.keys(updatedGridCells).forEach((studentKey) => {
        Object.keys(updatedGridCells[studentKey]).forEach((topicKey) => {
            Object.keys(updatedGridCells[studentKey][topicKey]).forEach((activityKey) => {
                if (prevGridCells?.[studentKey]?.[topicKey]?.[activityKey]) {
                    const updatedIsSelected = prevGridCells?.[studentKey]?.[topicKey]?.[activityKey].isSelected || false;

                    // Manages the Grid Cells
                    updatedGridCells[studentKey][topicKey][activityKey].isSelected = updatedIsSelected;
                    // Manages the Grid Counters
                    updatedGridCounters = manageFilteredGridCounters({
                        isSelected: updatedIsSelected,
                        updatedGridCounters,
                        studentKey,
                        topicKey,
                        activityKey
                    });
                    // Manages Selected Assignments
                    updatedSelectedAssignments = manageSelectedAssignmentsCallback({
                        selectedAssignments: updatedSelectedAssignments,
                        studentKey,
                        studentAssignments,
                        isSelected: updatedGridCells[studentKey][topicKey][activityKey].isSelected,
                        activityKey
                    });
                }
            });
        });
    });

    return {
        updatedGridCells,
        updatedGridCounters,
        updatedSelectedAssignments
    };
};
